import { CheckCircle, Clock, Download, Eye, FileText, Trash2, XCircle } from "lucide-react";
import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrderActions } from "@/hooks/useOrderActions";
import type { OrderWithDetails } from "@shared/schema";

/**
 * Per-order menu items rendered inside both the row right-click `ContextMenu`
 * and the per-row `...` `DropdownMenu` button. One source of truth — adding a
 * future action touches one place.
 *
 * Variant picks the Radix primitive set because Radix's Context-/Dropdown-Menu
 * items rely on their respective parent React contexts (a `ContextMenuItem`
 * inside a `DropdownMenuContent` loses keyboard/focus behavior).
 *
 * "Merge Orders" is intentionally NOT here — merge is selection-scoped and
 * stays in the toolbar `...` menu only.
 */
interface OrderActionItemsProps {
  order: OrderWithDetails;
  variant: "dropdown" | "context";
  onView: (order: OrderWithDetails) => void;
  onSendToQuickBooks: (orderId: number, confirmedResend?: boolean) => void | Promise<void>;
  onRequestDelete: (orderIds: number[]) => void;
}

export function OrderActionItems({
  order,
  variant,
  onView,
  onSendToQuickBooks,
  onRequestDelete,
}: OrderActionItemsProps) {
  const actions = useOrderActions({
    orderId: order.id,
    onSendToQuickBooks,
    onRequestDelete,
  });

  const Item = variant === "context" ? ContextMenuItem : DropdownMenuItem;
  const Sub = variant === "context" ? ContextMenuSub : DropdownMenuSub;
  const SubTrigger = variant === "context" ? ContextMenuSubTrigger : DropdownMenuSubTrigger;
  const SubContent = variant === "context" ? ContextMenuSubContent : DropdownMenuSubContent;
  const Separator = variant === "context" ? ContextMenuSeparator : DropdownMenuSeparator;

  return (
    <>
      <Item onClick={() => onView(order)} className="cursor-pointer">
        <Eye className="mr-2 h-4 w-4" />
        View Order
      </Item>
      <Item onClick={() => actions.printPickSheet()} className="cursor-pointer">
        <Download className="mr-2 h-4 w-4" />
        Print Pick-Sheet
      </Item>
      {/* QB-26: a cancelled order is never sendable — the server refuses it too. */}
      {order.status !== "cancelled" && (
        <Item onClick={() => actions.sendToQuickBooks()} className="cursor-pointer">
          <FileText className="mr-2 h-4 w-4" />
          Send to QuickBooks
        </Item>
      )}
      <Separator />
      <Sub>
        <SubTrigger className="cursor-pointer">
          <CheckCircle className="mr-2 h-4 w-4" />
          Set Status
        </SubTrigger>
        <SubContent>
          <Item
            onClick={() => actions.setStatus("pending")}
            className="cursor-pointer"
            disabled={order.status === "pending"}
          >
            <Clock className="mr-2 h-4 w-4 text-blue-600" />
            Pending
          </Item>
          <Item
            onClick={() => actions.setStatus("reviewed")}
            className="cursor-pointer"
            disabled={order.status === "reviewed"}
          >
            <Eye className="mr-2 h-4 w-4 text-orange-600" />
            Reviewed
          </Item>
        </SubContent>
      </Sub>
      {order.status !== "cancelled" && (
        <Item
          onClick={() => actions.cancelOrder()}
          className="cursor-pointer text-red-600"
        >
          <XCircle className="mr-2 h-4 w-4" />
          Cancel Order
        </Item>
      )}
      <Separator />
      <Item
        onClick={() => actions.requestDelete()}
        className="cursor-pointer text-red-600"
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Delete Order
      </Item>
    </>
  );
}
